<template>
  <v-row v-if="show" no-gutters class="nav-bar ma-0">
    <!-- Workpackage name and back nav -->
    <v-col class="bg-white workpackage d-flex justify-start">
      <v-btn
        class="btn-back"
        primary
        size="small"
        rounded="0"
        variant="flat"
        :to="{ name: backNavRoute }"
      >
        <v-icon size="28" color="background"> mdi-chevron-left </v-icon>
      </v-btn>
      <v-toolbar-title class="pa-2 pl-4">
        {{ workpackage.name }}
      </v-toolbar-title>
    </v-col>

    <!-- Scenario drop down selector -->
    <v-col v-if="enableScenarioSelection" class="scenarioSelect d-flex align-center">
      <v-select
        :model-value="selectedScenario"
        flat
        :items="scenarios"
        :menu-props="{ bottom: true, offsetY: true }"
        item-color="#2f477c"
        item-title="name"
        item-value="_id"
        variant="solo"
        hide-details
        density="compact"
        single-line
        return-object
        @update:model-value="changeScenarios"
      >
        <template #item="{ item, props: itemProps }">
          <ActionableLink
            v-bind="itemProps"
            component="v-list-item"
            :to="createScenarioLink(item)"
            :on-click="scenarioAction"
          >
            <v-list-item v-bind="itemProps" />
          </ActionableLink>
        </template>
      </v-select>
    </v-col>

    <!-- navigation tabs + users -->
    <v-col class="tabs__container align-center" data-id-e2e="tabsRoute">
      <p v-if="tabsTitle" class="tabs__title ml-4 mr-3 my-0">
        {{ tabsTitle }}
      </p>

      <v-tabs
        :hide-slider="hideSlider"
        height="34"
        class="nav-bar-tabs"
        :model-value="selectedTabPosition"
        @update:model-value="onTabChange"
      >
        <v-tab
          v-for="tab in tabData"
          :key="tab.routeName"
          :ripple="false"
          :disabled="isTabDisabled(tab)"
          :to="createRouteForTab(tab)"
          :replace="true"
          :value="tab.position"
          min-width="0"
          :title="$t(tab.translationKey)"
        >
          {{ $t(tab.translationKey) }}
          <v-icon v-if="showStatuses" class="status-tab-icon" size="18"> ${{ tab.status }} </v-icon>
        </v-tab>
      </v-tabs>

      <UsersPermissions v-if="showNotImplemented" hide-title justify-center class="panel-cell" />

      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <div
            v-if="selectedWorkpackage.type === 'a_fresh'"
            v-bind="props"
            class="fresh-indicator d-flex align-center justify-end"
          >
            <v-icon size="26" class="icon" primary> mdi-leaf </v-icon>
          </div>
        </template>
        <span>{{ $t('general.freshLong') }}</span>
      </v-tooltip>
    </v-col>

    <!-- Expansion -->
    <v-col v-if="enableNotes" class="d-flex justify-end expand-btn-container align-center">
      <Notes
        :notes="getAllNotes"
        :loading="isNotesLoading"
        :title="$t('notes.types.scenario')"
        :max-chars="getClientConfig.notesSettings.maxLength"
        :notifications="!!selectedScenario.totalNotes"
        @post="postNewNote"
        @menu-open="fetchAllNotes"
        @delete="deleteCurrentNote"
        @update="updateCurrentNote"
      />
      <v-icon size="35" class="icon" primary> mdi-chevron-down </v-icon>
    </v-col>
  </v-row>
</template>

<script>
// Main toolbar used on the non-homepage pages.

import { mapActions, mapState } from 'pinia';
import { get, isFunction, has } from 'lodash';

// utils
import tabUtils from '@/utils/tabs/tabs-utils';
import scenarioTabs from '@/utils/tabs/scenario-tabs-enum';

// enums
import sectionStatuses from '@enums/section-status';
import contextEnum from '@enums/note-contexts';

// composables
import { useRouter, useRoute } from 'vue-router';
import { useNotes } from '@/composables/notes';
import { useToolbarNavigation } from '@/composables/use-toolbar-navigation';

// stores
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useScenariosStore } from '@/stores/scenarios';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';

export default {
  props: {
    backNavRoute: {
      type: String,
      required: true,
    },

    enableScenarioSelection: {
      type: Boolean,
      required: false,
      default: false,
    },

    enableNotes: {
      type: Boolean,
      required: false,
      default: false,
    },

    hideSlider: {
      type: Boolean,
      required: false,
      default: true,
    },

    tabsTitle: {
      type: String,
      required: false,
      default: '',
    },

    toolbarTabs: {
      type: Object,
      required: true,
    },

    workpackage: {
      type: Object,
      required: true,
    },

    showStatuses: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  setup(props) {
    const router = useRouter();
    const route = useRoute();

    const {
      isNotesLoading,
      getAllNotes,
      getNotes,
      postNewNote,
      fetchAllNotes,
      deleteCurrentNote,
      updateCurrentNote,
    } = useNotes(contextEnum.scenario, {
      id: route.params.scenarioId,
      context: contextEnum.scenario,
      workpackageId: props.workpackage._id,
    });

    const { registerNavigationTargets, clearNavigationTargets } = useToolbarNavigation();

    return {
      router,
      route,
      registerNavigationTargets,
      clearNavigationTargets,
      // notes
      isNotesLoading,
      getAllNotes,
      getNotes,
      postNewNote,
      fetchAllNotes,
      deleteCurrentNote,
      updateCurrentNote,
    };
  },

  data() {
    return {
      currentTab: null,
      previousTab: null,
      clickedTab: null,
      isNoteLoading: false,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'selectedAssortmentCanvas',
      'assortmentCanvases',
      'fullScreenExpanded',
      'workpackageAssortmentCanvasesScenarioMap',
    ]),
    ...mapState(useContextStore, [
      'allowTabChange',
      'showNotImplemented',
      'getDateFormats',
      'getClientConfig',
    ]),
    ...mapState(useScenariosStore, ['selectedScenario', 'scenarios', 'selectedScenarioStatus']),
    ...mapState(useWorkpackagesStore, ['selectedWorkpackage', 'selectedWorkpackageStatus']),

    getScenarioStatus() {
      return this.selectedScenarioStatus(this.selectedWorkpackage.type);
    },

    tabData() {
      const tabData = tabUtils.getByType(this.toolbarTabs.ALL, this.selectedWorkpackage.type);
      // Update tab data with status
      tabData.forEach(tab => {
        const statusSection =
          get(this.selectedScenarioStatus, tab.statusSection) ||
          get(this.selectedWorkpackageStatus, tab.statusSection);

        if (statusSection) {
          // Disable the assortment panel when there are no canvases till modelling page is completed
          const isAssortmentTab = tab.id === this.toolbarTabs.ASSORTMENTS_PANEL_TAB;
          const isStatusNotStarted = statusSection.status === sectionStatuses.notStarted;
          const productModellingStatus = get(
            this.selectedScenarioStatus,
            'modelling.productModelling.status'
          );
          const isSectionStatusesCompleted = productModellingStatus === sectionStatuses.complete;
          if (isAssortmentTab && isStatusNotStarted && !isSectionStatusesCompleted) {
            tab.deactivated = true;
          } else {
            tab.deactivated = false;
          }
          tab.status = statusSection.status;
          tab.available = statusSection.available;
        } else {
          tab.status = sectionStatuses.notStarted;
          tab.available = false;
        }
      });
      return tabData;
    },

    /**
     * Flat list of all navigable routes for the current scenario, used by the AI agent.
     * Includes: home, top-level tabs that ARE their own page (firstChild === null),
     * and all scenario sub-tabs.
     * Top-level tabs with sub-pages (firstChild !== null) are excluded — they redirect
     * to their first child and have no page of their own.
     */
    allNavigationTargets() {
      const targets = [];

      // Home page — always reachable from any scenario page
      targets.push({
        routeName: 'HomePage',
        label: this.$t('navBar.workpackages'),
        available: true,
      });

      // Top-level tabs that are their own destination (no sub-tabs), e.g. assortments-panel
      this.tabData.forEach(tab => {
        if (tab.firstChild === null) {
          targets.push({
            routeName: tab.routeName,
            label: this.$t(tab.translationKey),
            available: !!tab.available && !tab.disabled && !tab.deactivated,
          });
        }
      });

      // All scenario sub-tabs (leaf pages), filtered to the current workpackage type
      const filteredSubTabs = scenarioTabs.ALL.filter(
        tab => tab.wpTypes && tab.wpTypes.includes(this.selectedWorkpackage.type)
      );
      filteredSubTabs.forEach(tab => {
        const sectionStatus = get(this.selectedScenarioStatus, tab.statusSection);
        const subStatus =
          sectionStatus && tab.statusSubSection ? get(sectionStatus, tab.statusSubSection) : null;
        targets.push({
          routeName: tab.routeName,
          label: this.$t(tab.translationKey),
          available: subStatus ? !!subStatus.available : false,
        });
      });

      return targets;
    },

    selectedTab() {
      return this.toolbarTabs.ALL.find(tab => tab && this.route.path.includes(tab.id));
    },

    selectedTabPosition() {
      return this.selectedTab ? this.selectedTab.position : 0;
    },

    availableCanvasId() {
      if (!this.assortmentCanvases.length) return null;
      if (
        this.selectedAssortmentCanvas._id &&
        this.selectedAssortmentCanvas.scenarioId === this.route.params.scenarioId
      )
        return this.selectedAssortmentCanvas._id;
      return this.assortmentCanvases[0]._id;
    },

    show() {
      return !(
        this.selectedAssortmentCanvas._id &&
        this.selectedAssortmentCanvas.scenarioId === this.route.params.scenarioId &&
        this.fullScreenExpanded
      );
    },

    isAssortmentRoute() {
      return ['assortments-panel', 'assortments-canvas'].includes(this.route.name);
    },
  },

  watch: {
    allNavigationTargets: {
      handler(targets) {
        this.registerNavigationTargets(targets);
      },
      immediate: true,
    },
  },

  beforeUnmount() {
    this.clearNavigationTargets();
  },

  created() {
    if (this.enableScenarioSelection) this.getWpScenarios(this.workpackage._id);
    if (!this.selectedTab) return;

    let tabRoute;
    if (isFunction(this.selectedTab.firstChild)) {
      tabRoute = this.selectedTab.firstChild(this.selectedScenarioStatus, this.getClientConfig);
    } else {
      tabRoute = this.selectedTab.firstChild || this.selectedTab.routeName;
    }
    this.currentTab = this.router.resolve({
      name: tabRoute,
      params: this.route.params,
    }).href;
    this.previousTab = this.currentTab;
    const toolbar = this;
    this.emitter.on('unsaved-data-confirm', () => {
      toolbar.onTabChange(toolbar.clickedTab);
    });
  },

  methods: {
    ...mapActions(useScenariosStore, ['fetchScenarios']),
    ...mapActions(useAssortmentCanvasStore, [
      'changeCanvasRenderingStatus',
      'setIsProductSidebarOpen',
      'setDashboardProduct',
    ]),

    async onTabChange(clickedTab) {
      this.clickedTab = clickedTab;
      await this.$nextTick();
      if (!this.allowTabChange) this.currentTab = this.previousTab;
      else this.previousTab = this.currentTab;
    },

    async getWpScenarios(workpackageId) {
      await this.fetchScenarios({ params: { where: { workpackageId } } });
    },

    createScenarioLink(scenario) {
      // Note we use `raw._id` instead of `_id` as we're receiving the wrapped Vuetify item
      // Return nothing if scenario is same as current scenario to avoid loading issues on assortment panel
      if (this.selectedScenario._id === scenario.raw._id) return {};
      if (this.isAssortmentRoute) {
        return {
          name: 'assortments-panel',
          params: { scenarioId: scenario.raw._id },
        };
      }
      return {
        name: this.route.name,
        params: { scenarioId: scenario.raw._id },
      };
    },

    scenarioAction() {
      if (this.isAssortmentRoute) {
        this.changeCanvasRenderingStatus(true);
      }
    },

    createRouteForTab(tab) {
      const { params, query } = this.route;

      if (this.toolbarTabs.TEMPLATE_INPUTS_TAB === tab.routeName) {
        // Cannot create template inputs route if scenario does not exist
        if (!this.selectedScenario._id) return;
        // Otherwise, ensure templateScenarioId param is set when navigating to template inputs tab
        if (
          !has(params, 'templateScenarioId') ||
          params.templateScenarioId !== this.selectedScenario._id
        ) {
          params.templateScenarioId = this.selectedScenario._id;
        }
      }

      let tabRoute;
      if (isFunction(tab.firstChild)) {
        tabRoute = tab.firstChild(this.selectedScenarioStatus, this.getClientConfig);
      } else {
        tabRoute = tab.firstChild;
      }
      return { name: tabRoute || tab.routeName, params, query };
    },

    isTabDisabled(tab) {
      return !tab.available || !!tab.disabled || !!tab.deactivated;
    },

    changeScenarios() {
      this.setIsProductSidebarOpen(false);
      this.setDashboardProduct(null);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.nav-bar {
  border-top: 1px solid $assortment-primary-colour;
  border-bottom: 1px solid $assortment-navbar-sub-divider-colour;
  background-color: $assortment-navbar-colour;
  margin-bottom: 20px;
  height: 42px;
  box-sizing: border-box;
  display: flex;
  flex: 0;

  .workpackage {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    flex: 0 0 24%;
    max-width: 24%;
  }

  .row {
    height: 100%;

    .v-btn {
      height: 100%;
      width: 28px;
    }
  }

  .v-toolbar__title {
    font-size: 1.2rem;
    font-weight: bold;
  }

  .tabs {
    &__container {
      display: flex;
      flex: 0 0 43%;
      height: 100%;
      max-width: 43%;
    }

    &__title {
      color: $assortment-navigation-tab-text-colour;
      font-size: 1.2rem;
      font-weight: bold;
      white-space: nowrap;
    }
  }

  .v-tab {
    display: flex;
    justify-content: space-between;
    font-size: 1.2rem;
    font-family: $assortment-regular-font;
    color: $assortment-navigation-tab-text-colour;
    padding: 0 7px 0 9px;
    align-items: center;
    padding-bottom: 1px;
    min-width: 15px;

    &--active {
      background-color: $assortment-navbar-colour !important;
    }

    &::before {
      opacity: 0 !important;
      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
    }

    &::after {
      content: '';
      position: absolute;
      right: 0;
      width: 1px;
      border-right: 2px solid $assortment-navbar-main-divider-colour;
      transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
    }

    &:nth-child(2) {
      &::after {
        bottom: 5px;
        height: 24px;
      }

      &::before {
        content: '';
        position: absolute;
        right: 0;
        bottom: 5px;
        width: 1px;
        top: auto !important;
        height: 24px;
        border-right: 2px solid $assortment-navbar-main-divider-colour;
        opacity: 1 !important;
        display: block;
        visibility: visible !important;
      }
    }

    &:nth-child(3),
    &:nth-child(4) {
      &::after {
        bottom: 7px;
        height: 20px;
        border-right: 1px solid $assortment-navbar-sub-divider-colour;
      }
    }

    &:nth-child(5),
    &:nth-child(6),
    &:nth-child(7) {
      &::after {
        bottom: 5px;
        top: auto !important;
        height: 24px;
      }
    }
  }

  :deep(.v-item-group) {
    background-color: $assortment-navbar-colour;
  }

  :deep(.v-input) {
    border-radius: 0;
  }

  :deep(.v-input__slot) {
    height: 25px;
    border-bottom: 1px solid $assortment-primary-colour;
  }

  :deep(.v-text-field .v-input__control) {
    min-height: auto;
    border-radius: 0;
  }

  :deep(.v-ripple_container) {
    display: none;
  }

  .nav-bar-tabs {
    margin-right: 9px;
    background: $assortment-navbar-colour;

    .v-icon {
      margin-left: 1px;
    }

    :deep(.v-slide-group__next),
    :deep(.v-slide-group__prev) {
      .v-icon {
        color: $assortment-primary-colour;
      }
    }
  }

  .icon {
    color: $assortment-primary-colour;
    cursor: pointer;
  }

  .fresh-indicator {
    font-size: 1.3rem;
    margin-right: 10px;

    .icon {
      margin-right: 4px;
      color: green !important;
    }
  }

  .expand-btn-container {
    flex: 0 0 12%;
    max-width: 12%;
    :deep(button) {
      margin-right: 1.2rem;
    }

    .v-icon {
      position: relative;
      margin-right: 2px;
      &::after {
        content: '';
        position: absolute;
        left: -2px;
        bottom: 4px;
        width: 1px;
        height: 27px;
        border-right: 1px solid $assortment-navbar-main-divider-colour;
      }
    }
  }
}
.scenarioSelect {
  padding: 0 12px !important;
  flex: 0 0 21%;
  max-width: 21%;

  :deep(.v-input__slot) {
    padding-right: 0px !important;
    padding-left: 5px !important;
  }

  :deep(.v-select__selection.v-select__selection--comma) {
    font-size: 1.2rem !important;
  }

  :deep(.v-field--active) {
    border-bottom: 2px solid $assortment-primary-colour;
  }
}
.v-tab {
  text-transform: none;
}

.btn-back {
  height: 100%;
  width: 28px;
}

.status-tab-icon {
  margin-left: 4px !important;
}
</style>
